import { ModifyTask, TaskFull } from 'interfaces/task.interface';
import { CommonUserService, dayjs, Equipment, LoggerService, ShortUser } from '@umanforge/common';

import { Calendar, DayLayoutFunction, SlotInfo, ViewsProps } from 'react-big-calendar';
import withDragAndDrop, { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FORMAT_DATE_API, RootState } from '@umanforge/common';
import { CustomWeek, DayView, WeekViewWeekEnd } from './weekView.hook';
import { CalendarEvent } from 'interfaces/calendar.interface';
import eventStyleGetter from './event.hook';
import { SectorSettings } from 'interfaces/setting.interface';
import { ToastService } from 'services/toast.service';
import { useTranslation } from 'react-i18next';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import { RoleTaskFull } from 'interfaces/roleTask.interface';
import { RoleTaskService } from 'services/roleTask.service';
import { useDispatch, useSelector } from 'react-redux';
import { TaskUserUtils } from 'utils/taskUser.utils';
import { TaskEquipment } from 'interfaces/task_equipment.interface';
import EventComponent from '../components/event.component';
import { dayLayoutAlgorithm } from 'utils/dayLayoutAlgorithm/noOverlap.utils';
import { RecurrenceService } from 'services/recurrence.service';
import { ModifyRecurrence } from 'interfaces/recurrence.interface';
import { TaskService } from 'services/task.service';
import { Tooltip } from 'react-tooltip';
import { Warning } from 'interfaces/warning.interface';
import { TimeGrid } from 'interfaces/timegrid.interface';

const toastService: ToastService = new ToastService( '' );

export interface AbsenceTaskUser {
  taskId: string;
  user: ShortUser
}

const usePlanningComponent = (
  tasks: TaskFull[],
  currentDate: dayjs.Dayjs,
  timegrid: TimeGrid,
  equipments: Equipment[],
  equipmentsTasks: TaskEquipment[],
  taskActionImages: Map<string, string | null>,
  onUpdate: ( task: ModifyTask, refreshTask?: boolean ) => Promise<boolean>,
  settings?: SectorSettings,
  fixed: boolean = false,
  isDayView: boolean = false,
  showOnlyMyTasks: boolean = false,
  highlightMyTasks: boolean = false,
  refreshTask: () => Promise<void> = ( ) => {return Promise.resolve(); },
  onWarnings: ( absences: Warning[] ) => void = () => { /** */ },
  openTaskModal?: string
) => {

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const me = useSelector( ( state: RootState ) => {return state.user.value;} );
  const logger: LoggerService = new LoggerService( 'Planning Hook' );

  const [ calendarEvents, setCalendarEvents ] = useState<CalendarEvent[]>( [] );
  const [ isModalOpen, setIsModalOpen ] = useState( false );
  const [ currentEvent, setCurrentEvent ] = useState<CalendarEvent>(  );
  const [ timeStartDrag, setTimeStartDrag ] = useState<dayjs.Dayjs>(  );
  const [ timeEndDrag, setTimeEndDrag ] = useState<dayjs.Dayjs>(  );
  const [ dateDrag, setDateDrag ] = useState<dayjs.Dayjs>(  );
  const [ allRoleTasks, setAllRoleTasks ] = useState<Map<string, RoleTaskFull[]>>( new Map() );
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ isAbsenceChecked, setIsAbsenceChecked ] = useState( false );
  const [ absenceUsers, setAbsenceUser ] = useState<AbsenceTaskUser[]>( [] );
  const [ absenceWarnings, setAbsenceWarnings ] = useState<Warning[]>( [] );
  const [ equipmentsWarnings, setEquipmentsWarnings ] = useState<Warning[]>( [] );
  const [ usersWarnings, setUsersWarnings ] = useState<Warning[]>( [] );

  const [ confirmationRecurrenceModalOpen, setConfirmationRecurrenceModalOpen ] = useState<EventInteractionArgs<CalendarEvent>>( );
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ isDeleteRecurrenceModalOpen, setIsDeleteRecurrenceModalOpen ] = useState( false );


  const [ allDayEventOver, setAllDayEventOver ] = useState<string | undefined>( undefined );

  const colors = [ '#F94144', '#F3722C', '#F9C74F', '#9B5DE5', '#43AA8B', '#90DBF4', '#577590', '#F15BB5' ];
  const formats = { dayFormat: 'dddd DD/MM' };

  const [ contextMenu, setContextMenu ] = useState<{ mouseX: number; mouseY: number; task: TaskFull | undefined } | null>( null );
  const menuRef = useRef<HTMLDivElement | null>( null );

  // ---------- USE MEMO ------------

  const myDayLayoutAlgorithm: DayLayoutFunction<CalendarEvent> = useMemo( () => {
    return ( {
      events,
      minimumStartDifference,
      slotMetrics,
      accessors
    } ) => {
      const allDayEvents = events.filter( ev => { return ev.allDayIndicator; } );
      const styledDayEvents = dayLayoutAlgorithm( {
        events: allDayEvents,
        minimumStartDifference,
        slotMetrics,
        accessors,
        offset: 0
      } ).map( event => {
        return {
          ...event,
          style: {
            ...event.style,
            width: '0.5rem', // minimal possible
          },
        };
      } );

      let maxIdx = styledDayEvents.length;
      if ( styledDayEvents.length > 1 ) {
        maxIdx = Math.max( ...( styledDayEvents.map( event => { return event.idx; } ) as number[] ) ) + 1;
      }

      return [ ...styledDayEvents, ...dayLayoutAlgorithm( {
        events: events.filter( ev => { return !ev.allDayIndicator; } ),
        minimumStartDifference,
        slotMetrics,
        accessors,
        offset: maxIdx
      } ) ];
    };
  }, [] );


  const filteredTasks = useMemo( () => {
    if ( !showOnlyMyTasks || !me ) {
      return tasks;
    }
    return TaskUserUtils.filterTasksByUser( tasks, me.id, allRoleTasks );
  }, [ tasks, showOnlyMyTasks, me, allRoleTasks ] );


  const { eventPropGetter } = useMemo( () => {
    return  {
      eventPropGetter: ( event : CalendarEvent ) => {
        return eventStyleGetter( event, highlightMyTasks, me, allRoleTasks, showOnlyMyTasks );
      }
    };
  }, [ me, allRoleTasks, highlightMyTasks, allDayEventOver ] );


  const trigger = ( id: string | undefined ) => {
    if ( !isFirstRender ) {
      const events = document.querySelectorAll( '[data-event="true"]' );
      const hasId = Boolean( id );
      const targetId = `task_${id}`;

      events.forEach( div => {
        const parent = div.closest( '.rbc-event[role="button"]' );
        if ( !parent ) { return; }

        if ( hasId ) {
          const isTarget = div.getAttribute( 'data-id' ) === targetId;
          parent.classList.toggle( 'opacity-50', !isTarget );
        } else {
          parent.classList.remove( 'opacity-50' );
        }
      } );
      setAllDayEventOver( id );
    }
  };

  const DnDCalendar = useMemo( () => {
    return fixed ? Calendar : withDragAndDrop( Calendar );
  }, [ fixed ] );


  const duplicateTask = async ( taskId: string | undefined ) => {
    if ( taskId != undefined ) {
      await TaskService.duplicateTaskById( taskId, token, dispatch );
      await refreshTask();
    }
  };

  const handleContextMenu = ( ctxEvent: React.MouseEvent, event: TaskFull | undefined ) => {
    ctxEvent.preventDefault();

    if ( contextMenu ) {
      setContextMenu( null );
    }
    else {
      setContextMenu( {
        mouseX: ctxEvent.clientX + 2,
        mouseY: ctxEvent.clientY - 6,
        task: event
      } );
    }

    // Prevent text selection lost after opening the context menu on Safari and Firefox
    const selection = document.getSelection();
    if ( selection && selection.rangeCount > 0 ) {
      const range = selection.getRangeAt( 0 );

      setTimeout( () => { selection.addRange( range ); } );
    }
  };

  const { components } = useMemo( () => {
    const EventWithContext = ( props: { event: CalendarEvent } ) => {
      const title = props.event.task?.taskAction?.name || '';
      const users: ShortUser[] = [];
      absenceUsers.map( atu => {
        if ( atu.taskId == props.event.task?.id ) {
          users.push( atu.user );
        }
      } );

      const component = <EventComponent
        event={props.event}
        title={title}
        allRoleTasks={allRoleTasks}
        equipmentStack={ checkEquipmentStack( props.event.task ) }
        taskActionImage={taskActionImages.get( props.event.task?.taskAction.id ?? '' )}
        setAllDayOver={ trigger }
        usersAbsence={ users }
      />;

      const userList = allRoleTasks.get( props.event?.task?.id ?? '' )?.map( rt => {
        return rt.users;
      } ).flat() ?? [];

      return (
        <div id={ 'tootltip_anchor_' + props.event?.id + '' } className='h-full'>
          {
            props.event.task?.status == 'COMING' && !fixed ?
              <div onContextMenu={ ctxEvent => { handleContextMenu( ctxEvent, props.event.task ); } } className='w-full h-full' >
                { component }
              </div>
              : component
          }
          { userList.length > 0 &&
            <Tooltip
              anchorSelect={ '#tootltip_anchor_' + props.event?.id }
              className='z-[1000] '
              opacity={0.75}
              place="right"
              positionStrategy='fixed'
            >
              { userList.map( user => { return <p key={ 'tooltip_user_' + props.event?.id + '_' + user.id }>{ user.fullname }</p>; } ) }
            </Tooltip>
          }
        </div>
      );
    };

    return  {
      components: { event: EventWithContext },
    };
  }, [ allRoleTasks, taskActionImages, isFirstRender, calendarEvents, absenceUsers ] );

  const { min, max, step, timeslots } = useMemo( () => {
    let minHour = DEFAULT_VALUES.HOURSTART;
    let maxHour = DEFAULT_VALUES.HOUREND;

    const timedridStartDate = dayjs( timegrid.timeStart );
    const timedridEndDate = dayjs( timegrid.timeEnd );
    const tzDiff = dayjs().utcOffset() / 60;

    const timegridStart = ( timedridStartDate.hour() * 60 ) + timedridStartDate.minute();
    const timegridEnd = ( timedridEndDate.hour() * 60 ) + timedridEndDate.minute();

    if ( timegrid && timegridStart < timegridEnd ) {
      minHour = timedridStartDate.hour();
      maxHour = timedridEndDate.hour();
    }
    else if ( timegrid && timegridStart > timegridEnd ) {
      minHour = 0;
      maxHour = 24;
    }

    tasks.forEach( task => {
      // if not fullDay task ( to avoid timeslot going from 0h to 24h )
      if ( !( dayjs( task.dateStart ).format( 'HH:mm' ) === '00:00' && dayjs( task.dateEnd ).format( 'HH:mm' ) === '23:59' ) ) {
        const taskStartHour = dayjs( task.dateStart ).hour();
        const taskEndHour = dayjs( task.dateEnd ).hour();

        if ( taskStartHour < minHour ) {
          minHour = taskStartHour;
        }
        if ( taskEndHour + 1 > maxHour ) {
          maxHour = taskEndHour + 1;
        }
        if ( ( taskStartHour + 1 ) > maxHour ) {
          maxHour = taskStartHour + 1;
        }
        if ( taskEndHour < minHour ) {
          minHour = taskEndHour;
        }
      }
    } );

    return  {
      min: dayjs( `1970-1-0 ${ minHour + tzDiff }:00` ).toDate(),
      max: maxHour < 24 ? dayjs( `1970-1-0 ${ maxHour + tzDiff }:00` ).toDate() : dayjs( '1972-1-0 23:59' ).toDate(),
      step: settings?.calendarStep?.value ?? DEFAULT_VALUES.STEP,
      timeslots: Math.max( 1, Math.floor( 30 / Math.max( 1, settings?.calendarStep?.value ?? DEFAULT_VALUES.STEP ) ) ),
    };
  }, [ settings, tasks, timegrid ] );

  const customTimeSlot = useMemo( () => {
    const custom = ( date: Date ) => {
      const dateHour =  dayjs( date ).format( 'HH:mm' );
      if ( timegrid && timegrid.timeStart > timegrid.timeEnd ) {
        const virtualBreaktimeStart = dayjs( timegrid.timeStart ).format( 'HH:mm' );
        const virtualBreaktimeEnd = dayjs( timegrid.timeEnd ).format( 'HH:mm' );

        if ( dateHour < virtualBreaktimeStart && dateHour >= virtualBreaktimeEnd ) {
          return { style: {
            backgroundColor: '#E8E8E8'
          } };
        }
      }
      else {
        if ( dateHour < dayjs( timegrid.timeStart ).format( 'HH:mm' ) || dateHour >= dayjs( timegrid.timeEnd ).format( 'HH:mm' ) ) {
          return { style: {
            backgroundColor: '#E8E8E8'
          } };
        }
      }

      if ( timegrid.breaktimes.length > 0 ) {
        if ( timegrid.breaktimes.some( breaktime => {
          return !( dateHour < dayjs( breaktime.timeStart ).format( 'HH:mm' ) || dateHour >= dayjs( breaktime.timeEnd ).format( 'HH:mm' ) );
        } ) ) {
          return { style: {
            backgroundColor: '#E8E8E8'
          } };
        }
      }
      return {};
    };
    return custom;
  }, [ timegrid ] );

  const { views } = useMemo( () => {
    let view = CustomWeek;
    if ( isDayView ) {
      view = DayView;
    }
    else if ( settings?.showWeekend?.value == 1 ) {
      view = WeekViewWeekEnd;
    }
    return  {
      views: { week: view } as ViewsProps<object, object> | undefined
    };
  }, [ settings, isDayView ] );


  // ---------- USE EFFECT ------------

  useEffect( () => {
    init();
  }, [ ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const fetchAllRoleTasks = async () => {
        setAllDayEventOver( undefined );
        const roleTasksMap = new Map<string, RoleTaskFull[]>();

        if ( !token || tasks.length === 0 ) {
          setAbsenceUser( [] );
          setAbsenceWarnings( [] );
          setEquipmentsWarnings( [] );
          setAllRoleTasks( roleTasksMap );
          return;
        }


        await Promise.all(
          tasks.map( async task => {
            const roleTasks = await RoleTaskService.getRoleTasksFullByTaskId(
              task.id,
              token,
              dispatch,
              error => {
                logger.error( `Error fetching role tasks for task ${task.id}:`, error );
                roleTasksMap.set( task.id, [] );
              },
              warning => {
                logger.warn( `Warning fetching role tasks for task ${task.id}:`, warning );
                roleTasksMap.set( task.id, [] );
              }
            );
            roleTasksMap.set( task.id, roleTasks );
          } )
        );

        setAllRoleTasks( roleTasksMap );
        await checkAbsences( roleTasksMap );
      };

      fetchAllRoleTasks();
    }
  }, [ tasks ] );

  useEffect( () => {
    onWarnings( [ ...absenceWarnings, ...equipmentsWarnings, ...usersWarnings ] );
  }, [ absenceWarnings, equipmentsWarnings, usersWarnings ] );

  useEffect( () => {
    checkEquipmentTasksWarnings( equipmentsTasks );
  }, [ equipmentsTasks ] );

  useEffect( () => {
    checkUserTasksWarnings( allRoleTasks );
  }, [ allRoleTasks ] );

  useEffect( () => {
    const openTaskModalReq = async ( taskId: string ) => {
      const _task = await TaskService.getTaskFullById( taskId, token, dispatch );
      if ( _task ) {
        handleSelectEvent( {
          id: openTaskModal,
          task: _task
        } as CalendarEvent );
      }
    };

    if ( openTaskModal != null ) {
      const _task = tasks.find( ta => { return ta.id == openTaskModal; } );
      if ( !_task ) {
        openTaskModalReq( openTaskModal );
      }
      else {
        handleSelectEvent( {
          id: openTaskModal,
          task: _task
        } as CalendarEvent );
      }
    }
  }, [ openTaskModal ] );


  const userIsAbsentOnTask = ( userAbsences: { startDate: string; endDate: string; }[] | null, taskId: string ): boolean => {
    if ( userAbsences != null && userAbsences.length > 0 && tasks && tasks.length > 0 ) {
      const task = tasks.find( ta => { return ta.id == taskId; } );
      if ( task ) {
        const startTask = dayjs( task.dateStart );
        const endTask = dayjs( task.dateEnd );
        return userAbsences.some( ( abs: { startDate: string; endDate: string; } ) => {
          const start = dayjs( abs.startDate ).startOf( 'day' );
          const end = dayjs( abs.endDate ).endOf( 'day' );
          return (
            ( startTask.isAfter( start ) && startTask.isBefore( end ) ) ||
            ( endTask.isAfter( start ) && endTask.isBefore( end ) ) ||
            ( startTask.isBefore( start ) && endTask.isAfter( end ) )
          );
        } );
      }
    }
    return false;
  };

  const checkAbsences = async ( roleTasks: Map<string, RoleTaskFull[]> ) => {
    if ( isAbsenceChecked ) {
      return;
    }

    const taskUsers: AbsenceTaskUser[] = [];
    const _absenceWarnings: Warning[] = [];

    if ( tasks.length > 0 && roleTasks.size > 0 ) {
      setIsAbsenceChecked( true );

      const usersId: string[] = [ ...new Set( Array.from( roleTasks.values() ).flat().map( rt => {
        return rt.users.map( u => { return u.id; } );
      } ).flat() ) ];

      if ( usersId.length > 0 ) {
        try {
          const range = { start: currentDate.format( FORMAT_DATE_API ), end: currentDate.add( 6, 'days' ).format( FORMAT_DATE_API ) };
          const absenceData = await CommonUserService.getAbsenceDaysOfUsers( usersId, range, token, dispatch );
          const absenceByTasks: Map<string, string[]> = new Map();

          if ( Object.keys( absenceData ).length > 0 ) {
            roleTasks.forEach( ( rt, taskId )=> {
              rt.forEach( r => {

                r.users.forEach( user => {
                  if ( userIsAbsentOnTask( absenceData[ user.id ], taskId ) ) {
                    taskUsers.push( { taskId: taskId, user: user } );
                    absenceByTasks.set( taskId, [ ...new Set( [ ...absenceByTasks.get( taskId ) ?? [], user.fullname ] ) ] );
                  }
                } );
              } );
            } );

            absenceByTasks.forEach( ( abt, taskId ) => {
              const task = tasks.find( ta => { return taskId == ta.id; } );
              if ( task  ) {
                if ( abt.length > 1 ) {
                  // const text = `${ abt.join( ', ' )} ${t( 'translation:are_absent_on_the_task_date', {
                  //   taskName: task.taskAction.name,
                  //   date: dayjs( task.dateStart ).format( 'll' ),
                  //   hour: dayjs( task.dateStart ).format( 'LT' )
                  // } )} `;
                  // _absenceWarnings.push( {
                  //   title: '',
                  //   description: text,
                  //   taskId: task.id
                  // } as Warning );
                }
                else {
                  // const text = t( 'translation:is_absent_on_the_task_date', {
                  //   fullname: abt[ 0 ],
                  //   taskName: task.taskAction.name,
                  //   date: dayjs( task.dateStart ).format( 'll' ),
                  //   hour: dayjs( task.dateStart ).format( 'LT' )
                  // } );
                  // _absenceWarnings.push( {
                  //   title: '',
                  //   description: text,
                  //   taskId: task.id
                  // } as Warning );
                }
              }
            } );
          }
        }
        catch ( error ) {
          logger.error( 'Error checking absences:', error );
          setAbsenceUser( [] );
          setAbsenceWarnings( [] );
        }
      }
    }
    setAbsenceUser( taskUsers );
    setAbsenceWarnings( _absenceWarnings );
  };

  useEffect( () => {
    if ( !isFirstRender ) {
      setIsAbsenceChecked( false );
    }
  }, [ currentDate, settings ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const resCalendarEvents: CalendarEvent[]  = [];

      let colorIdx = 0;
      filteredTasks.forEach( task => {
        const event = new CalendarEvent( task );

        const multipleDay = !dayjs( task.dateStart ).startOf( 'day' ).isSame( dayjs( task.dateEnd ).startOf( 'day' ) );
        const fullDay = ( dayjs( task.dateStart ).format( 'HH:mm' ) === '00:00' && dayjs( task.dateEnd ).format( 'HH:mm' ) === '23:59' );
        if ( multipleDay || fullDay ) {
          colorIdx++;
          event.allDayColor = colors[ colorIdx % colors.length ];

          // multiDay
          if ( multipleDay ) {
            resCalendarEvents.push( ...splitAllDayEvent( {
              ...event,
              allDayIndicator: true,
              allDay: false
            } ) );
          }

          // fullDayEvent
          else {
            resCalendarEvents.push( {
              ...event,
              allDayIndicator: true,
              allDay: false,
              fullDay: fullDay,
              id: event.id + '_fullday'
            } );
          }

          resCalendarEvents.push( {
            ...event,
            allDay: true
          } );
        }
        else {
          resCalendarEvents.push( event );
        }
      } );
      setCalendarEvents( resCalendarEvents );
    }
  }, [ filteredTasks, isFirstRender ] );


  // ---------- FUNCTION ------------

  const init = () => {
    const handleClickOutside = ( event: MouseEvent | Event ) => {
      if ( menuRef.current && !menuRef.current.contains( event.target as Node ) ) {
        setContextMenu( null );
      }
    };

    const onBlur = ( event: FocusEvent ) => {
      if ( 'sourceCapabilities' in event && event.sourceCapabilities == null ) {
        if ( menuRef.current && !menuRef.current.contains( event.target as Node ) ) {
          setContextMenu( null );
        }
      }
    };

    document.addEventListener( 'mousedown', handleClickOutside );
    document.addEventListener( 'scroll', handleClickOutside );
    window.addEventListener( 'focusout', onBlur );

    setIsFirstRender( false );

    return () => {
      document.removeEventListener( 'mousedown', handleClickOutside );
      document.addEventListener( 'scroll', handleClickOutside );
      window.removeEventListener( 'focusout', onBlur );
    };
  };

  const splitAllDayEvent = ( event: CalendarEvent ): CalendarEvent[] => {
    const res: CalendarEvent[] = [];
    if ( event.task ) {
      const dateStart = dayjs( event.task.dateStart );
      const dateEnd = dayjs( event.task.dateEnd );

      if ( dateStart.isAfter( dateEnd ) ) {
        return [];
      }

      res.push( {
        ...event,
        id: event.id + '_bckGrnd_start',
        start: dateStart.toDate(),
        end: dateStart.endOf( 'day' ).toDate()
      } );

      let day = dateStart.startOf( 'day' ).add( 1, 'day' );
      while ( !day.isSame( dateEnd.startOf( 'day' ) ) ) {
        res.push( {
          ...event,
          id: event.id + '_bckGrnd_' + day.day(),
          start: day.toDate(),
          end: day.endOf( 'day' ).toDate(),
          resizable: false,
          draggable: false
        } );
        day = day.add( 1, 'day' );
      }

      res.push( {
        ...event,
        id: event.id + '_bckGrnd_end',
        start: dateEnd.startOf( 'day' ).toDate(),
        end: dateEnd.toDate(),
        allDayLast: true
      } );
    }
    return res;
  };

  const checkEquipmentTasksWarnings = ( _equipmentsTasks: TaskEquipment[] ) => {
    if ( !_equipmentsTasks || _equipmentsTasks.length == 0 ) {
      setEquipmentsWarnings( [] );
      return;
    }

    const stackedEquipments: Equipment[] = [];
    const stackedEquipmentsWarning: Warning[] = [];
    tasks.forEach( task => {
      const taskEquipmentsNames = [ ...new Set( checkEquipmentStack( task ).map( equip => { return equip.name; } ) ) ];
      if ( taskEquipmentsNames.length > 1 ) {
        // const text = `${ taskEquipmentsNames.join( ', ' )} ${t( 'translation:are_absent_on_the_task_date', {
        //   taskName: task.taskAction.name,
        //   date: dayjs( task.dateStart ).format( 'll' ),
        //   hour: dayjs( task.dateStart ).format( 'LT' )
        // } )} `;
        // stackedEquipmentsWarning.push( {
        //   title: '',
        //   description: text,
        //   taskId: task.id
        // } as Warning );
      }
      else if ( taskEquipmentsNames.length > 0 ) {
        // const text = t( 'translation:is_absent_on_the_task_date', {
        //   fullname: taskEquipmentsNames[ 0 ],
        //   taskName: task.taskAction.name,
        //   date: dayjs( task.dateStart ).format( 'll' ),
        //   hour: dayjs( task.dateStart ).format( 'LT' )
        // } );
        // stackedEquipmentsWarning.push( {
        //   title: '',
        //   description: text,
        //   taskId: task.id
        // } as Warning );
      }
    } );
    setEquipmentsWarnings( stackedEquipmentsWarning );
    return stackedEquipments;
  };

  const checkUserTasksWarnings = ( _allRoleTasks: Map<string, RoleTaskFull[]> ) => {
    if ( !_allRoleTasks ) {
      setUsersWarnings( [] );
      return;
    }

    const stackedUsersWarning: Warning[] = [];
    tasks.forEach( task => {
      const missingUser = TaskUserUtils.isTaskMissingOperators( task, _allRoleTasks );
      if ( missingUser > 1 ) {
        // stackedUsersWarning.push( {
        //   title: '',
        //   description: t( 'translation:operateurs_needed_on_the_task_number', { number: missingUser } ) + ' ' + task.taskAction.name,
        //   taskId: task.id
        // } as Warning );
      }
      else if ( missingUser == 1 ) {
        // stackedUsersWarning.push( {
        //   title: '',
        //   description: t( 'translation:operateur_needed_on_the_task_number' ) + ' ' + task.taskAction.name,
        //   taskId: task.id
        // } as Warning );
      }
    } );
    setUsersWarnings( stackedUsersWarning );
  };

  const checkEquipmentStack = ( task: TaskFull | undefined ): Equipment[] => {
    if ( !task || !equipmentsTasks || equipmentsTasks.length == 0 ) {
      return [];
    }

    const stackedEquipments: Equipment[] = [];
    const taskEquipmentId: string[] = [];
    equipmentsTasks.map( taskEquip => {
      if ( task.id == taskEquip.idTask ) {
        taskEquipmentId.push( taskEquip.idEquipment );
      }
    } );

    for ( const calEvent of calendarEvents ) {
      if ( task.id != calEvent.id && calEvent.end && calEvent.start && task.dateEnd && task.dateStart ) {
        if ( !( calEvent.start?.valueOf() >= dayjs( task.dateEnd )?.valueOf() || calEvent.end?.valueOf() <= dayjs( task.dateStart )?.valueOf() ) ) {
          stackedEquipments.push( ...equipments.filter( equip => { return taskEquipmentId.includes( equip.id ); } ) );
        }
      }
    }
    return stackedEquipments;
  };

  const handleSelectSlot = useCallback(
    ( { start, end }: SlotInfo ) => {
      if ( isModalOpen ) {
        return;
      }
      if ( dayjs( start ) > dayjs() ) {
        setCurrentEvent( undefined );
        setTimeStartDrag( dayjs( start ) );
        setTimeEndDrag( dayjs( end ) );
        setDateDrag( dayjs( start ) );
        setIsModalOpen( true );
      }
      else {
        toastService.showError( t( 'translation:errors.you_cannot_create_task_in_the_past' ) );
      }
    },
    [ isModalOpen ]
  );


  const handleSelectEvent = useCallback( ( event : CalendarEvent ) => {
    if ( !contextMenu ) {
      setCurrentEvent( event );
      setIsModalOpen( true );
    }
  }, [ contextMenu ] );

  const updateOnDrag = ( { event, start, end }: EventInteractionArgs<CalendarEvent> ) => {
    if ( event.allDayIndicator && event.allDayLast ) {
      toastService.showError( t( 'translation:errors.drag_not_allowed_to_this_part_of_task' ) );
      return;
    }
    updateTask( { event, start, end } );
  };

  const updateOnResize = ( { event, start, end }: EventInteractionArgs<CalendarEvent> ) => {
    if ( event.allDayIndicator ) {
      if ( event.allDayLast ) {
        start = dayjs( event.task?.dateStart ).toDate();
      }
      else {
        end = dayjs( event.task?.dateEnd ).toDate();
      }
    }
    updateTask( { event, start, end } );
  };

  const updateTask = async ( { event, start, end }: EventInteractionArgs<CalendarEvent>, isConfirmed: boolean = false, updateRecurrence: boolean = false ) => {
    if ( event.task ) {
      if ( event.task.status == 'DONE' ) {
        toastService.showError( t( 'translation:errors.you_cannot_modify_validate_task' ) );
        return;
      }
      if ( event.task.status == 'CANCELED' ) {
        toastService.showError( t( 'translation:errors.you_cannot_modify_canceled_task' ) );
        return;
      }

      const startTime = dayjs( start );
      const endTime = dayjs( end );
      const changedDay = !startTime.isSame( dayjs( event.task.dateStart ), 'day' ) || !endTime.isSame( dayjs( event.task.dateEnd ), 'day' );

      if ( !isConfirmed && event.task.idRecurrence != null ) {
        const recurrence = await RecurrenceService.getRecurrenceById( event.task.idRecurrence, token, dispatch )
          .catch( () => {return undefined;} );

        const shouldAskRecurrenceConfirmation = !( recurrence?.type === 'daily' || recurrence?.type === 'weekly' ) || !changedDay;

        if ( shouldAskRecurrenceConfirmation ) {
          setConfirmationRecurrenceModalOpen( { event, start, end } );
          return;
        }
      }

      if ( startTime <= dayjs() || endTime <= dayjs() ) {
        toastService.showError( t( 'translation:errors.you_cannot_move_task_in_the_past' ) );
        return;
      }

      const originalTaskDateStart = event.task.dateStart;
      const originalTaskDateEnd = event.task.dateEnd;

      event.task.dateStart = startTime.toISOString();
      event.task.dateEnd = endTime.toISOString();

      // update planning before API request
      //const filtered = calendarEvents.filter( ev => { return ev.id !== event.id; } );
      //setCalendarEvents( [ ...filtered, { ...event } ] );

      const passed = await onUpdate( event.task, false );

      try {
        if ( event.task.id && passed && updateRecurrence && event.task.idRecurrence ) {
          const recurrence = await RecurrenceService.getRecurrenceById( event.task.idRecurrence, token, dispatch )
            .catch( () => {return undefined;} );

          const getMonthlyOccurrenceRank = ( date: dayjs.Dayjs ): number => {
            const weekday = date.day();
            const monthStart = date.startOf( 'month' );
            const monthEnd = date.endOf( 'month' );
            const offsetFromStart = ( weekday - monthStart.day() + 7 ) % 7;
            const firstOccurrence = monthStart.add( offsetFromStart, 'day' );
            const lastWeekday = monthEnd.subtract( ( monthEnd.day() - weekday + 7 ) % 7, 'day' );

            if ( date.isSame( lastWeekday, 'day' ) ) {
              return 0;
            }

            const occurrenceIndex = Math.floor( ( date.date() - firstOccurrence.date() ) / 7 ) + 1;
            return Math.min( 5, Math.max( 1, occurrenceIndex ) );
          };

          const getMonthlyWeekdayAnchor = ( date: dayjs.Dayjs, occurrence: number ): dayjs.Dayjs => {
            const monthStart = date.startOf( 'month' );
            const weekday = date.day();
            const monthEnd = date.endOf( 'month' );

            if ( occurrence === 0 ) {
              return monthEnd.subtract( ( monthEnd.day() - weekday + 7 ) % 7, 'day' );
            }

            const firstOccurrenceOffset = ( weekday - monthStart.day() + 7 ) % 7;
            const firstOccurrence = monthStart.add( firstOccurrenceOffset, 'day' );
            return firstOccurrence.add( ( occurrence - 1 ) * 7, 'day' );
          };

          const taskId = event.task?.id ?? '';

          const getMonthlyRecurrencePayload = ( date: dayjs.Dayjs, recurrenceData?: typeof recurrence ): ModifyRecurrence => {
            const basePayload: ModifyRecurrence = {
              taskId,
              value: recurrenceData?.value ?? 0,
              type: recurrenceData?.type ?? 'daily',
              repeat: recurrenceData?.repeat ?? 1,
              repeatType: recurrenceData?.repeatType ?? 1,
            };

            if ( recurrenceData?.type !== 'monthly' ) {
              return basePayload;
            }

            if ( recurrenceData.repeatType === 1 ) {
              basePayload.value = date.date();
              return basePayload;
            }

            if ( recurrenceData.repeatType === 2 ) {
              const nextOccurrence = getMonthlyOccurrenceRank( date );
              basePayload.value = nextOccurrence;
              basePayload.dateStart = getMonthlyWeekdayAnchor( date, nextOccurrence ).toISOString();
              basePayload.dateEnd = recurrenceData.dateEnd ?? date.toISOString();
              return basePayload;
            }

            return basePayload;
          };

          const nextRecurrencePayload = getMonthlyRecurrencePayload( startTime, recurrence );
          const recurrenceWindowChanged = recurrence?.type === 'monthly' || ( recurrence && (
            recurrence.dateStart !== originalTaskDateStart ||
            recurrence.dateEnd !== originalTaskDateEnd
          ) );

          if ( recurrenceWindowChanged ) {
            if ( recurrence?.type === 'monthly' ) {
              const monthlyPayload = getMonthlyRecurrencePayload( startTime, recurrence );
              Object.assign( nextRecurrencePayload, monthlyPayload );
            }
            else {
              nextRecurrencePayload.dateStart = recurrence?.dateStart ?? originalTaskDateStart;
              nextRecurrencePayload.dateEnd = recurrence?.dateEnd ?? originalTaskDateEnd;
            }
          }

          await RecurrenceService.putRecurrence(
            event.task.idRecurrence,
            nextRecurrencePayload,
            token,
            dispatch
          ).catch( () => { return undefined; } );
        }
      }
      catch ( error ) {
        logger.error( 'Error updating recurrence on drag/drop:', error );
      }
      finally {
        await refreshTask();
      }
    }
  };

  const handleDelete = async ( updateRecurrence: boolean = false ) => {
    if ( currentEvent?.task ) {
      if ( currentEvent.task.idRecurrence && updateRecurrence ) {
        await RecurrenceService.deleteRecurrence( currentEvent.task.idRecurrence, token, dispatch );
        setCurrentEvent( undefined );
        refreshTask();
      }
      else {
        await TaskService.deleteTask( currentEvent.task.id, token, dispatch );
        setCurrentEvent( undefined );
        refreshTask();
      }
    }
  };


  return {
    DnDCalendar,
    min,
    max,
    components,
    eventPropGetter,
    formats,
    step,
    timeslots,
    calendarEvents,
    isModalOpen,
    currentEvent, setCurrentEvent,
    timeStartDrag,
    timeEndDrag,
    dateDrag,
    views,
    updateTask,
    updateOnDrag,
    updateOnResize,
    handleSelectEvent,
    handleSelectSlot,
    setTimeStartDrag,
    setTimeEndDrag,
    setDateDrag,
    setIsModalOpen,
    customTimeSlot,
    myDayLayoutAlgorithm,
    confirmationRecurrenceModalOpen, setConfirmationRecurrenceModalOpen,
    isDeleteModalOpen, setIsDeleteModalOpen,
    isDeleteRecurrenceModalOpen, setIsDeleteRecurrenceModalOpen,
    handleDelete,
    contextMenu, setContextMenu,
    menuRef,
    duplicateTask
  };
};

export default usePlanningComponent;